/**
 * The JC ladder as a prospect form presents it.
 *
 * `utils/roster-class.ts` owns the type, the three values and the wording ("JC 2"). This module
 * owns the two things a form additionally needs - the option list and the reverse lookup off a
 * label - so that neither gets re-derived inline in a feature component, which is exactly the
 * duplication `constants/playerLevels.ts` exists to undo.
 *
 * It sits here rather than in `roster-class.ts` because that module is a deliberate three-way
 * copy kept in step with mobile and the API parser; a web-only option list has no place in it.
 */

import { JUCO_YEARS, formatJucoYear } from "../utils/roster-class";
import type { JucoYear } from "../utils/roster-class";

/** The labels a coach picks from, in ladder order: "JC 1", "JC 2", "JC 3". */
export const JUCO_YEAR_LABEL_OPTIONS: readonly string[] = JUCO_YEARS.map(
  // `?? year` only satisfies the `string | null` signature - `formatJucoYear` cannot return null
  // for a member of `JUCO_YEARS`.
  (year) => formatJucoYear(year) ?? year,
);

/** `{ label, value }` options for `components/Dropdown`, which stores the label as its value. */
export const JUCO_YEAR_DROPDOWN_OPTIONS: { label: string; value: string }[] =
  JUCO_YEAR_LABEL_OPTIONS.map((label) => ({ label, value: label }));

/** The bare year behind a label, or `null` when the label names no JC year. */
export function jucoYearFromLabel(
  label: string | null | undefined,
): JucoYear | null {
  if (!label) return null;
  return JUCO_YEARS.find((year) => formatJucoYear(year) === label) ?? null;
}
